import { ReactNode, useEffect, useState } from "react";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import TextField from "@mui/material/TextField";
import { BDC_CANCEL_REASON_MAX } from "./constants";
import { checkCancelReason } from "./Invoice/invoiceNo";

/**
 * ยกเลิก with a reason (1 … 500 characters) — CN and ใบวางบิล. `onConfirm` makes the call and
 * resolves true when it went through (the interceptor shows a refusal; the dialog then stays).
 */
export default function ReasonDialog({
  open,
  title,
  warning,
  confirmLabel,
  onClose,
  onConfirm,
}: Readonly<{
  open: boolean;
  title: string;
  warning: ReactNode;
  confirmLabel: string;
  onClose: () => void;
  onConfirm: (reason: string) => Promise<boolean>;
}>) {
  const [reason, setReason] = useState("");
  const [touched, setTouched] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setReason("");
      setTouched(false);
    }
  }, [open]);

  const error = checkCancelReason(reason);

  const submit = async () => {
    setTouched(true);
    if (error) return;
    setSaving(true);
    try {
      if (await onConfirm(reason.trim())) onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Alert severity="warning" sx={{ mb: 2 }}>
          {warning}
        </Alert>
        <TextField
          autoFocus
          fullWidth
          multiline
          minRows={2}
          label="เหตุผลที่ยกเลิก"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          onBlur={() => setTouched(true)}
          error={touched && Boolean(error)}
          helperText={touched && error ? error : `${reason.trim().length}/${BDC_CANCEL_REASON_MAX}`}
          slotProps={{ htmlInput: { maxLength: BDC_CANCEL_REASON_MAX + 50 } }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          ไม่ยกเลิก
        </Button>
        <Button color="error" variant="contained" onClick={submit} loading={saving} disabled={touched && Boolean(error)}>
          {confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
